import { useWidget } from '../../../providers/widget'
import { PropertyCardWidgetConfig } from '../config'
import { useFetchCardProperty } from '../data'
import TemplateClassic from '../../../common/property-card/TemplateClassic'
import TemplateOverlay from '../../../common/property-card/TemplateOverlay'
import TemplateDetailed from '../../../common/property-card/TemplateDetailed'
import TemplateExplorerAdapter from './TemplateExplorerAdapter'
import CardSkeleton from '../../../common/property-card/CardSkeleton'
import Stylesheet from '../../../components/Stylesheet'
import WebFont from '../../../components/WebFont'
import WidgetDataError from '../../../components/WidgetDataError'
import { resolveWidgetSpacingStyle, clampPercentToFull } from "@homerunner-next/widget-core/spacing"

const TEMPLATES = {
  classic: TemplateClassic,
  overlay: TemplateOverlay,
  detailed: TemplateDetailed,
  explorer: TemplateExplorerAdapter,
} as const

/**
 * Dark `--hfse-*` overrides for the auto FIRST-PAINT path (#900, mirrors
 * useExplorerCss). The base cssString below always emits the resolved (LIGHT for
 * a light/SSR host) palette on `.hr-property-card`; this appends the DARK set
 * under two selector prefixes so light output stays byte-identical:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark and
 *     post-hydration auto).
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * Values come from `options.darkModeColors` directly (not gated on
 * resolvedTheme) so they ship on every render. `:host(...)` (not a descendant
 * `[data-hr-theme]`) is required to reach the shadow host from inside its own
 * stylesheet; BOTH auto conditions live INSIDE `:host(...)` — a `:not` placed
 * after the parens silently never matches the host.
 */
function propertyCardDarkOverrides(
  prefix: string,
  dc: PropertyCardWidgetConfig['darkModeColors'],
): string {
  return `
    ${prefix} .hr-property-card {
      --hfse-accent: ${dc.accent};
      --hfse-foreground: ${dc.foreground};
      --hfse-muted-foreground: ${dc.mutedForeground};
      --hfse-card-bg: ${dc.cardBackground};
      --hfse-card-fg: ${dc.cardForeground};
      --hfse-border: ${dc.borderColor};
    }
  `
}

export function PropertyCardWidgetContent() {
  const { options, resolvedTheme } = useWidget<PropertyCardWidgetConfig>()
  const { data: property, isLoading, isError, error } = useFetchCardProperty(options.filter)

  const Template = TEMPLATES[options.template] ?? TemplateClassic
  const colors = resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  const typography = options.typography

  // #824: per-element sizes replace the base font.size knob, which never
  // applied here — the card's CSS is rem-based, and rem resolves against the
  // host document's root, not the shadow :host this stylesheet can reach.
  const cssString = `
    .hr-property-card {
      --hfse-accent: ${colors.accent};
      --hfse-foreground: ${colors.foreground};
      --hfse-muted-foreground: ${colors.mutedForeground};
      --hfse-card-bg: ${colors.cardBackground};
      --hfse-card-fg: ${colors.cardForeground};
      --hfse-border: ${colors.borderColor};
      --hr-pc-fs-title: ${typography.titleSize}px;
      --hr-pc-fs-location: ${typography.locationSize}px;
      --hr-pc-fs-price: ${typography.priceSize}px;
      --hr-pc-fs-excerpt: ${typography.excerptSize}px;
      --hr-pc-fs-meta: ${typography.metaSize}px;
      --hr-pc-fs-chip: ${typography.chipSize}px;
    }
    .hr-property-card *, .hr-property-card {
      font-family: '${options.font.family}' !important;
    }
    :host {
      background-color: transparent !important;
    }
    ${propertyCardDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
    @media (prefers-color-scheme: dark) {
      ${propertyCardDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
    }
    ${options.customCss ?? ''}
  `

  // A percentage wider than the container is meaningless and lets a stored
  // value like 150% overflow the host page — cap % at 100, leave px alone.
  const spacingStyle = resolveWidgetSpacingStyle(options)
  const cardStyle: React.CSSProperties = {
    ...spacingStyle,
    maxWidth: clampPercentToFull(spacingStyle.maxWidth),
  }

  return (
    <div
      className={`hfse hr-property-card ${resolvedTheme === 'dark' ? 'dark' : ''}`}
      style={cardStyle}
      suppressHydrationWarning
    >
      <WebFont />
      <Stylesheet css={cssString} />
      {isLoading ? <CardSkeleton aspectRatio={options.imageAspectRatio} /> : null}
      {isError ? <WidgetDataError error={error} /> : null}
      {!isLoading && !isError && property ? (
        <Template property={property} options={options} />
      ) : null}
    </div>
  )
}
